import Layout from "components/layout";
import { resetScroll } from "utils/browser";
import { useMounted } from "hooks/react";
import Link from "next/link";
import { FiArrowLeft } from "react-icons/fi";
import { useSubscription } from "hooks/subscription";
import { useChatLimits } from "hooks/chat_limits";
import clsx from "clsx";

const conversationLimit = 15;

export default function ChatLimitsPage(): JSX.Element {
  useMounted(resetScroll);
  const {
    isActive,
    isTrial,
    isLoading: isLoadingSubscription
  } = useSubscription();
  const {
    allLimits,
    userLimits,
    usedLimits,
    isLoading: isLoadingLimits
  } = useChatLimits();

  const isLoading = isLoadingSubscription || isLoadingLimits;

  function currentPlanMessage() {
    if (isTrial) {
      return "Você está no período de teste do Vstiba Total e tem acesso limitado ao Chat de Questões:";
    }
    if (isActive) {
      return "Como usuário do Vstiba Total, você tem acesso a limites ampliados:";
    }

    return "Como usuário do plano gratuito, você tem acesso limitado ao Chat de Questões:";
  }

  return (
    <Layout
      title="Limites do Chat de Questões"
      description="Entenda como funcionam os limites do Chat de Questões do Vstiba Total e como aproveitar ao máximo este recurso premium."
      canonical="/limites/chat"
    >
      <main>
        <div className="container">
          <div className="w-[40rem] mx-auto max-w-full md:my-12">
            <Link href="/assinatura">
              <a className="flex items-center mb-12 hover:text-emphasis-high text-emphasis-medium">
                <FiArrowLeft className="mr-1 w-5 h-5" />
                Ir para assinatura
              </a>
            </Link>
            <h1 className="mb-8 text-center text-emphasis-high text-4xl font-bold">
              Limites do Chat de Questões
            </h1>

            <div
              className={clsx(
                "fade-loading mb-8 p-8 bg-white border border-gray-100 rounded-xl shadow-lg",
                isLoading && "loading",
                isActive && !isTrial && "border-primary-200"
              )}
            >
              <h2 className="mb-4 text-2xl font-semibold">Seu limite atual</h2>

              <p className="mb-6 text-emphasis-medium">
                {currentPlanMessage()}
              </p>

              <div className="mb-6">
                <div className="bg-amber-50 border-amber-200 p-6 text-center border rounded-lg">
                  <div
                    className="text-amber-600 mb-2 text-5xl font-bold"
                    data-testid="daily-limit"
                  >
                    {userLimits?.questionChatMessages ||
                      allLimits?.free?.questionChatMessages ||
                      0}
                  </div>
                  <div className="mb-1 text-xl font-semibold">
                    mensagens por dia
                  </div>
                  <p className="text-emphasis-medium">
                    Seu limite diário total de mensagens
                  </p>
                </div>
              </div>

              <div className="mb-6 p-6 text-center border rounded-lg">
                <div className="mb-2 text-4xl font-bold">
                  {usedLimits?.questionChatMessages || 0} /{" "}
                  {userLimits?.questionChatMessages ||
                    allLimits?.free?.questionChatMessages ||
                    0}
                </div>
                <div className="mb-1 text-lg font-semibold">
                  mensagens usadas hoje
                </div>
              </div>
            </div>

            {(!isActive || isTrial) && (
              <div
                className={clsx(
                  "mb-8 p-8 bg-white border border-gray-100 rounded-xl shadow-lg",
                  isLoading && "hidden"
                )}
              >
                <h2 className="mb-4 text-2xl font-semibold">
                  Limites com Vstiba Total
                </h2>
                <p className="mb-6 text-emphasis-medium">
                  Com uma assinatura do Vstiba Total, você teria acesso a
                  limites muito maiores:
                </p>
                <div className="mb-6">
                  <div className="bg-amber-50 border-amber-200 p-6 text-center border rounded-lg">
                    <div className="mb-2 text-5xl font-bold">
                      {allLimits?.paid?.questionChatMessages}
                    </div>
                    <div className="mb-1 text-xl font-semibold">
                      mensagens por dia
                    </div>
                    <p className="text-emphasis-medium">
                      Limite diário com Vstiba Total
                    </p>
                  </div>
                </div>

                <div className="bg-primary-50/30 mb-6 p-4 border border-primary-200 rounded-lg">
                  <p className="flex items-start text-emphasis-medium">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-amber-600 flex-shrink-0 mr-2 w-6 h-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <span>
                      Assine o <b>Vstiba Total</b> e receba{" "}
                      <b>
                        {allLimits?.paid?.questionChatMessages} mensagens por
                        dia
                      </b>
                      !
                    </span>
                  </p>
                </div>

                <div className="mt-6 text-center">
                  <Link href="/assinatura">
                    <a className="btn btn-primary">Assinar Vstiba Total</a>
                  </Link>
                </div>
              </div>
            )}

            <div className="mb-6 p-4 border border-secondary-100 rounded-lg">
              <p className="flex items-start text-emphasis-medium">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0 mr-2 w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  <b>Importante:</b> Os limites são reiniciados diariamente à
                  meia-noite, então você recebe um novo lote de mensagens a cada
                  dia.
                </span>
              </p>
            </div>
            <p className="text-emphasis-medium">
              * O limite máximo de mensagens por questão é {conversationLimit}.
            </p>
          </div>
        </div>
      </main>
    </Layout>
  );
}
